<template>
  <VPopover
    trigger="click"
    placement="bottom"
    class="mod-nbbo"
    @show="showHandler"
    @hide="hideHandler"
  >
    <Icon v-tooltip="title" class="nbbo-icon" name="nbbo" />
    <div slot="popover" class="nbbo-popover">
      <div class="nbbo-title">
        {{ title }}
      </div>
      <div v-if="isLoading" class="nbbo-loading">
        <Loading size="sm" />
      </div>
      <div v-else class="nbbo-content">
        <div v-for="item in items" :key="item.key" class="nbbo-item">
          <span class="nbbo-label">{{ item.label }}</span>
          <span class="nbbo-value">{{ item.value }}</span>
        </div>
      </div>
    </div>
  </VPopover>
</template>
<script>
import Icon from 'com/Icon'
import Loading from 'com/Loading'
import { fetchNBBOMarketData } from 'apis/stock'
import { VPopover } from 'v-tooltip'
import { debounce } from 'lodash'
import { US_EXCHANGE_SHORT_CODE_MAP } from 'const/trade'

export default {
  name: 'NbboIcon',
  components: { Icon, Loading, VPopover },
  props: {
    symbol: String
  },
  data() {
    return {
      isLoading: false,
      nbboMarketData: {}
    }
  },
  computed: {
    title() {
      return `${this.symbol} ${this.$t('quote.nbbo-market-data')}`
    },
    latestText() {
      if (this.nbboMarketData.latestPrice) {
        const { largeNumber } = this.$options.filters
        const priceSize = largeNumber(
          this.nbboMarketData.priceSize,
          2,
          0,
          true,
          'en_US'
        )
        return `$${this.formatPrice(
          this.nbboMarketData.latestPrice
        )} x${priceSize}`
      }
      return '--'
    },
    bidText() {
      const { largeNumber } = this.$options.filters

      if (this.nbboMarketData.bidPrice) {
        return `$${this.formatPrice(
          this.nbboMarketData.bidPrice
        )} x${largeNumber(this.nbboMarketData.bidSize)}`
      }
      return '--'
    },
    askText() {
      const { largeNumber } = this.$options.filters
      if (this.nbboMarketData.askPrice) {
        return `$${this.formatPrice(
          this.nbboMarketData.askPrice
        )} x${largeNumber(this.nbboMarketData.askSize)}`
      }
      return '--'
    },
    items() {
      const exch = US_EXCHANGE_SHORT_CODE_MAP[this.nbboMarketData.partCode]
      const askExch =
        US_EXCHANGE_SHORT_CODE_MAP[this.nbboMarketData.askPartCode]
      const bidExch =
        US_EXCHANGE_SHORT_CODE_MAP[this.nbboMarketData.bidPartCode]
      return [
        {
          key: 'scale',
          label: `${this.$t('quote.nbbo-last-sale')}${
            exch ? ` (${exch})` : ''
          }`,
          value: this.latestText
        },
        {
          key: 'bid',
          label: `${this.$t('quote.nbbo-best-bid')}${
            bidExch ? ` (${bidExch})` : ''
          }`,
          value: this.bidText
        },
        {
          key: 'ask',
          label: `${this.$t('quote.nbbo-best-ask')}${
            askExch ? ` (${askExch})` : ''
          }`,
          value: this.askText
        }
      ]
    }
  },
  methods: {
    showHandler() {
      this.debouncedFetchData =
        this.debouncedFetchData ||
        debounce(() => {
          this.isLoading = true
          return fetchNBBOMarketData(this.symbol)
            .then(data => {
              this.nbboMarketData = data
            })
            .finally(() => {
              this.isLoading = false
            })
        }, 150)
      this.debouncedFetchData()
    },
    hideHandler() {
      this.nbboMarketData = {}
      if (this.debouncedFetchData) {
        this.debouncedFetchData.cancel()
        this.debouncedFetchData = null
      }
    },
    formatPrice(price) {
      const digits = price < 1 ? 4 : 2
      return price.toFixed(digits)
    }
  }
}
</script>
<style lang="less" scoped>
.mod-nbbo {
  display: flex;
  align-items: center;
  width: 14px;
  height: 14px;
  margin-left: 5px;
  ::v-deep .trigger {
    width: 14px;
    height: 14px;
    line-height: 1;
  }
}
.nbbo-popover {
  width: 200px;
  height: 90px;
}
.nbbo-icon {
  color: var(--sub-text-color);
  &:hover {
    color: var(--active-color);
  }
}
.nbbo-title {
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: bold;
}
.nbbo-item {
  display: flex;
  justify-content: space-between;
  line-height: 17px;
  margin-bottom: 5px;
  padding: 0;
}
.nbbo-label {
  color: var(--sub-text-color);
}
.nbbo-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px;
}
</style>
